/**
 * AdminFeedbackPanel — aba admin para ler feedbacks (ativos × desistentes)
 * + gerador de links públicos para os trials expirados (base da campanha de reativação).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Star } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stars = ({ n }) => (
  <span className="inline-flex">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-4 h-4 ${i <= n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
    ))}
  </span>
);

export default function AdminFeedbackPanel() {
  const [data, setData] = useState(null);
  const [context, setContext] = useState('');
  const [rating, setRating] = useState('');
  const [links, setLinks] = useState(null);
  const [loadingLinks, setLoadingLinks] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (context) params.context = context;
    if (rating) params.rating = rating;
    axios
      .get(`${API}/feedback/admin/list`, { params })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Erro ao carregar feedbacks'));
  }, [context, rating]);

  useEffect(() => {
    load();
  }, [load]);

  const generateLinks = async () => {
    setLoadingLinks(true);
    try {
      const r = await axios.post(`${API}/feedback/admin/generate-links`);
      setLinks(r.data);
      toast.success(`${r.data.count} links gerados/encontrados`);
    } catch {
      toast.error('Erro ao gerar links');
    } finally {
      setLoadingLinks(false);
    }
  };

  const copyLink = (token) => {
    navigator.clipboard.writeText(`${window.location.origin}/opine/${token}`);
    toast.success('Link copiado!');
  };

  const s = data?.summary;

  return (
    <div className="space-y-6" data-testid="admin-feedback-panel">
      <div>
        <h2 className="text-xl font-bold text-gray-800">💬 Feedbacks dos Usuários</h2>
        <p className="text-sm text-gray-500">Experiência de quem usa e motivos de quem desistiu — com recompensa de +7 dias por resposta.</p>
      </div>

      {s && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-gray-800" data-testid="feedback-total">{s.total}</p>
            <p className="text-xs text-gray-500">Total</p>
          </div>
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-amber-500">{s.avg_rating ?? '—'} ⭐</p>
            <p className="text-xs text-gray-500">Nota média</p>
          </div>
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-green-600">{s.ativos}</p>
            <p className="text-xs text-gray-500">De quem usa</p>
          </div>
          <div className="bg-white border rounded-lg p-3 text-center">
            <p className="text-2xl font-bold text-orange-600">{s.desistentes}</p>
            <p className="text-xs text-gray-500">De desistentes</p>
          </div>
        </div>
      )}

      {s && s.total > 0 && (
        <div className="bg-white border rounded-lg p-3">
          <p className="text-xs font-semibold text-gray-600 mb-2">Motivos de desistência:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(s.por_motivo).map(([label, count]) => (
              <span key={label} className={`text-xs px-2 py-1 rounded-full ${count > 0 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-400'}`}>
                {label}: {count}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <select
          value={context}
          onChange={(e) => setContext(e.target.value)}
          className="border rounded-md px-2 py-1.5 text-sm bg-white"
          data-testid="feedback-filter-context"
        >
          <option value="">Todos</option>
          <option value="ativo">Quem usa</option>
          <option value="desistente">Desistentes</option>
        </select>
        <select
          value={rating}
          onChange={(e) => setRating(e.target.value)}
          className="border rounded-md px-2 py-1.5 text-sm bg-white"
          data-testid="feedback-filter-rating"
        >
          <option value="">Todas as notas</option>
          {[5, 4, 3, 2, 1].map((n) => (
            <option key={n} value={n}>{n} estrelas</option>
          ))}
        </select>
      </div>

      <div className="space-y-3" data-testid="feedback-list">
        {(data?.items || []).length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">Nenhum feedback ainda.</p>
        )}
        {(data?.items || []).map((f) => (
          <div key={f.id} className="bg-white border rounded-lg p-4" data-testid="feedback-item">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-gray-800 text-sm">{f.name || f.email}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.user_type === 'employer' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                {f.user_type === 'employer' ? 'Proprietário' : 'Profissional'}
              </span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full ${f.context === 'desistente' ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                {f.context === 'desistente' ? 'Desistente' : 'Usando'}
              </span>
              {f.reward_days > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">🎁 +{f.reward_days}d</span>
              )}
              <span className="text-[10px] text-gray-400 ml-auto">
                {f.created_at ? new Date(f.created_at).toLocaleDateString('pt-BR') : ''}
                {f.source === 'link_publico' ? ' · via link' : ''}
              </span>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {f.rating && <Stars n={f.rating} />}
              {f.reason_label && (
                <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">{f.reason_label}</span>
              )}
            </div>
            {f.message && <p className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">{f.message}</p>}
          </div>
        ))}
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <p className="font-semibold text-green-800">📩 Links de feedback para os desistentes</p>
        <p className="text-xs text-green-700 mt-1">
          Gera um link único por usuário com trial expirado. Quem responder ganha +7 dias e volta a acessar.
          Copie e envie por WhatsApp, ou use na campanha de e-mail (próxima etapa).
        </p>
        <Button
          size="sm"
          className="mt-3 bg-green-600 hover:bg-green-700 w-full sm:w-auto"
          onClick={generateLinks}
          disabled={loadingLinks}
          data-testid="feedback-generate-links"
        >
          {loadingLinks ? 'Gerando...' : '🔗 Gerar links dos trials expirados'}
        </Button>

        {links && (
          <div className="mt-4 space-y-2" data-testid="feedback-links-list">
            <p className="text-xs text-gray-600 font-semibold">{links.count} usuários com trial expirado:</p>
            {links.links.map((l) => (
              <div key={l.token} className="bg-white border rounded-md p-2 flex flex-wrap items-center gap-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800 truncate">{l.name || l.email}</p>
                  <p className="text-xs text-gray-500 truncate">{l.email} · expirou há {l.days_expired}d</p>
                </div>
                {l.responded ? (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">✅ Respondeu</span>
                ) : (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Pendente</span>
                )}
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copyLink(l.token)} data-testid="feedback-copy-link">
                  📋 Copiar link
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
